// Shared formatters and color palette for EconomicIndicatorCard and
// EconomicIndicatorDetailModal. Kept in a separate module so the card and
// modal can render identical values without duplicating the format switch.

export const CATEGORY_STYLES = {
  Inflation: { bg: 'bg-amber-900/30', text: 'text-amber-300', stroke: '#fbbf24' },
  Labor:     { bg: 'bg-blue-900/30',  text: 'text-blue-300',  stroke: '#60a5fa' },
  Growth:    { bg: 'bg-emerald-900/30', text: 'text-emerald-300', stroke: '#34d399' },
  Consumer:  { bg: 'bg-violet-900/30', text: 'text-violet-300', stroke: '#a78bfa' },
  Industry:  { bg: 'bg-slate-700/40', text: 'text-slate-300', stroke: '#94a3b8' },
  Housing:   { bg: 'bg-rose-900/30',  text: 'text-rose-300',  stroke: '#fb7185' },
  Travel:    { bg: 'bg-sky-900/30',   text: 'text-sky-300',   stroke: '#38bdf8' },
  Auto:      { bg: 'bg-teal-900/30',  text: 'text-teal-300',  stroke: '#2dd4bf' },
};

export function fmtCurrent(value, format) {
  if (value == null) return '—';
  switch (format) {
    case 'pct':
      return `${value.toFixed(2)}%`;
    case 'pct_signed':
      return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
    case 'thousands':
      return `${Math.round(value).toLocaleString()}K`;
    case 'thousands_delta':
      return `${value >= 0 ? '+' : ''}${Math.round(value).toLocaleString()}K`;
    case 'billions_from_thousands':
      return `${(value / 1_000_000).toFixed(1)}B`;
    case 'count_millions':
      return `${value.toFixed(1)}M`;
    case 'decimal_ratio':
      return value.toFixed(2);
    default:
      return String(value);
  }
}

export function fmtChange(value, format) {
  if (value == null) return '';
  switch (format) {
    case 'pct':
    case 'pct_signed':
      return `${value >= 0 ? '+' : ''}${value.toFixed(2)} pp`;
    case 'thousands':
    case 'thousands_delta':
      return `${value >= 0 ? '+' : ''}${Math.round(value).toLocaleString()}K`;
    case 'billions_from_thousands':
      return `${value >= 0 ? '+' : ''}${(value / 1_000_000).toFixed(2)}B`;
    case 'count_millions':
      return `${value >= 0 ? '+' : ''}${value.toFixed(2)}M`;
    case 'decimal_ratio':
      return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
    default:
      return String(value);
  }
}

export function fmtMonth(iso) {
  if (!iso) return '—';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00Z' : ''));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function fmtDay(iso) {
  if (!iso) return '—';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00Z' : ''));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// EODHD release-table values come straight from their economic-events feed
// in their native units (e.g. Housing Starts as "1.465" millions, NFP as
// "115" thousands), which already match how the card displays the headline
// number. Use a single switch sized for EODHD's data.
export function fmtRelease(value, format) {
  if (value == null) return '—';
  switch (format) {
    case 'pct':
    case 'pct_signed':
      return `${value.toFixed(2)}%`;
    case 'thousands':
      // EODHD reports Housing Starts / Building Permits in millions, not
      // thousands — keep the M suffix to match.
      return `${value.toFixed(3)}M`;
    case 'thousands_delta':
      // NFP comes as raw thousand-count (e.g., 115 → 115K).
      return `${value >= 0 ? '+' : ''}${Math.round(value).toLocaleString()}K`;
    case 'billions_from_thousands':
      return `${value.toFixed(2)}B`;
    case 'count_millions':
      return `${value.toFixed(2)}M`;
    case 'decimal_ratio':
      return value.toFixed(2);
    default:
      return value.toLocaleString();
  }
}

// Signed delta of two release values, formatted compactly for the table.
export function fmtSurprise(actual, estimate, format) {
  if (actual == null || estimate == null) return '';
  const delta = actual - estimate;
  const sign = delta >= 0 ? '+' : '';
  switch (format) {
    case 'pct':
    case 'pct_signed':
      return `${sign}${delta.toFixed(2)} pp`;
    case 'thousands':
      return `${sign}${delta.toFixed(3)}M`;
    case 'thousands_delta':
      return `${sign}${Math.round(delta).toLocaleString()}K`;
    case 'billions_from_thousands':
      return `${sign}${delta.toFixed(2)}B`;
    case 'count_millions':
      return `${sign}${delta.toFixed(2)}M`;
    case 'decimal_ratio':
      return `${sign}${delta.toFixed(2)}`;
    default:
      return `${sign}${delta}`;
  }
}

export function changeColor(value, higherIsBad) {
  if (value == null) return 'text-gray-400';
  if (value >= 0) return higherIsBad ? 'text-red-400' : 'text-green-400';
  return higherIsBad ? 'text-green-400' : 'text-red-400';
}
